import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  TrendingUp,
  Schedule,
  CheckCircle,
  Warning,
  Hotel,
  LocationOn,
  KingBed,
  Send,
  Block,
  WarningAmber,
  CheckBox,
  Autorenew,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { brandColors } from '../../theme';

export const TrendReportDetailPage: React.FC = () => {
  const { trendReportId } = useParams();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hotelList, setHotelList] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const hasFetchedRef = useRef(false);
  const currentIdRef = useRef<string | undefined>(undefined);

  // Fetch report details on mount and when ID changes
  useEffect(() => {
    // Check if this is the same ID we already fetched
    if (currentIdRef.current === trendReportId && hasFetchedRef.current) {
      return;
    }
    
    // Update current ID and reset fetch flag if ID changed
    if (currentIdRef.current !== trendReportId) {
      currentIdRef.current = trendReportId;
      hasFetchedRef.current = false;
    }
    
    // Prevent duplicate fetches
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    
    const fetchReportDetails = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await authenticatedFetch(
          `${API_URL}/api/store/trend-reports/${trendReportId}`,
          { method: 'GET' }
        );

        if (response?.ok) {
          const data = await response.json();
          setReport(data.trendReport);
          // Load existing requirements if any
          if (data.trendReport?.hotelList || data.trendReport?.requirements) {
            setHotelList(data.trendReport.hotelList || data.trendReport.requirements);
          }
        } else if (response?.status === 404) {
          setError('Trend Report not found or access denied');
        } else {
          setError('Failed to load Trend Report details');
        }
      } catch (error) {
        console.error('Error fetching trend report:', error);
        setError('An error occurred while loading the report');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReportDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendReportId]);

  // Separate function for refreshing after submission
  const refreshReportDetails = useCallback(async () => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}`,
        { method: 'GET' }
      );

      if (response?.ok) {
        const data = await response.json();
        setReport(data.trendReport);
        // Load existing requirements if any
        if (data.trendReport?.hotelList || data.trendReport?.requirements) {
          setHotelList(data.trendReport.hotelList || data.trendReport.requirements);
        }
      }
    } catch (error) {
      console.error('Error refreshing trend report:', error);
    }
  }, [authenticatedFetch, trendReportId]);

  const handleSubmitHotelList = async () => {
    if (!hotelList.trim()) {
      alert('Please enter your hotel list before submitting');
      return;
    }

    setSubmitting(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/requirements`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            requirements: hotelList,
          }),
        }
      );

      if (response?.ok) {
        setSubmitSuccess(true);
        // Refresh report data
        await refreshReportDetails();
        // Show success message for 5 seconds
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        alert('Failed to submit requirements. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting requirements:', error);
      alert('An error occurred while submitting requirements');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'submitted':
        return <Schedule color="info" />;
      case 'needs_adjustments':
        return <Warning color="warning" />;
      default:
        return <TrendingUp />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'info' | 'warning' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'submitted':
        return 'info';
      case 'needs_adjustments':
        return 'warning';
      case 'purchased':
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          sx={{ mt: 2 }} 
          variant="outlined" 
          onClick={() => navigate('/subscriptions')}
          startIcon={<ArrowBack />}
        >
          Back to Subscriptions
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button 
        sx={{ mb: 3 }} 
        variant="outlined" 
        onClick={() => navigate('/subscriptions')}
        startIcon={<ArrowBack />}
      >
        Back to Your Purchases
      </Button>

      {/* Show hotel submission form when status is 'purchased' */}
      {report?.status === 'purchased' ? (
        <>
          <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <TrendingUp sx={{ fontSize: 40, color: brandColors.blue }} />
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{
                    color: brandColors.blue,
                    fontWeight: 'bold',
                  }}
                >
                  Submit Your Hotel List
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  MarketCompass Custom Trend Report - {report?.marketName}
                </Typography>
              </Box>
            </Box>

            {submitSuccess && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Your hotel list has been submitted successfully! We'll finalize your comp set and start building your report.
              </Alert>
            )}

            <Typography variant="body1" sx={{ mb: 3 }}>
              You're almost there—just tell us which hotels you'd like included in your trend report.
            </Typography>

            <Grid container spacing={3}>
              {/* Instructions Section */}
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', bgcolor: '#e3f2fd' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Hotel /> Step 1: Enter Your Hotels
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      For each hotel, include:
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Hotel fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Hotel Name" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <LocationOn fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Full Address (City, State, Country)" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <KingBed fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Number of Rooms" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', bgcolor: '#bbdefb' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Block /> Step 2: Follow the Rules
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Block fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="35% Max Rule" 
                          secondary="No one property/brand/owner can exceed 35% of total rooms"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <WarningAmber fontSize="small" color="warning" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Historical Changes" 
                          secondary="Reports may be truncated if room inventory changes"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', bgcolor: '#90caf9' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Autorenew /> Step 3: Provide Backups
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckBox fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Primary List" 
                          secondary="Your top preferred hotels"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Autorenew fontSize="small" color="info" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Secondary List" 
                          secondary="Backup hotels in order of preference"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                📝 Enter Your Hotels Below
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Type directly into the Trend Request box. A streamlined click-to-select hotel menu is coming soon!
              </Typography>
              
              <TextField
                multiline
                fullWidth
                rows={12}
                value={hotelList}
                onChange={(e) => setHotelList(e.target.value)}
                placeholder={`Example format:

PRIMARY & PREFERRED HOTELS:
1. Marriott Downtown - 123 Main St, New York, NY, USA - 450 rooms
2. Hilton Midtown - 456 Park Ave, New York, NY, USA - 380 rooms
3. Hyatt Times Square - 789 Broadway, New York, NY, USA - 320 rooms

SECONDARY FALL-BACK LIST:
1. Sheraton Manhattan - 321 5th Ave, New York, NY, USA - 280 rooms
2. W Hotel Union Square - 654 Union Sq, New York, NY, USA - 250 rooms`}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'monospace',
                    fontSize: '14px',
                  }
                }}
              />

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Once we have your list, our team will finalize your comp set and get started on your custom trend report right away.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Send />}
                  onClick={handleSubmitHotelList}
                  disabled={submitting || !hotelList.trim()}
                >
                  {submitting ? 'Submitting...' : 'Submit Hotel List'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </>
      ) : (
        /* Show existing view for other statuses */
        <Paper elevation={2} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
            <Box>
              <Typography 
                variant="h4" 
                gutterBottom
                sx={{
                  color: brandColors.blue,
                  fontWeight: 400,
                }}
              >
                Trend Report - {report?.marketName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                {getStatusIcon(report?.status)}
                <Chip 
                  label={report?.status?.replace('_', ' ').toUpperCase()} 
                  color={getStatusColor(report?.status)}
                />
              </Box>
            </Box>
            {report?.status === 'completed' && report?.reportUrl && (
              <Button
                variant="contained"
                startIcon={<Download />}
                href={report.reportUrl}
                target="_blank"
              >
                Download Report
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: brandColors.blue }}>
                    Purchase Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>Purchase Date:</strong> {new Date(report?.purchaseDate).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Amount:</strong> ${(report?.amount / 100).toFixed(2)} {report?.currency?.toUpperCase()}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Report ID:</strong> #{report?.id}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: brandColors.blue }}>
                    Processing Timeline
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {report?.submittedDate && (
                      <Typography variant="body2">
                        <strong>Submitted:</strong> {new Date(report.submittedDate).toLocaleDateString()}
                      </Typography>
                    )}
                    {report?.completedDate && (
                      <Typography variant="body2">
                        <strong>Completed:</strong> {new Date(report.completedDate).toLocaleDateString()}
                      </Typography>
                    )}
                    {!report?.submittedDate && report?.status !== 'purchased' && (
                      <Typography variant="body2" color="text.secondary">
                        Processing in progress
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {(report?.requirements || report?.hotelList) && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: brandColors.blue }}>
                      Submitted Hotel List
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {report.hotelList || report.requirements}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {report?.notes && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: brandColors.blue }}>
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {report.notes}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {report?.status === 'submitted' && (
            <Alert severity="info" sx={{ mt: 3 }}>
              Your hotel list has been received and is being processed. Your report will be delivered within 1 business day.
            </Alert>
          )}

          {report?.status === 'needs_adjustments' && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              Your Trend Report requires adjustments. Our team is working on the revisions.
            </Alert>
          )}

          {report?.status === 'completed' && !report?.reportUrl && (
            <Alert severity="info" sx={{ mt: 3 }}>
              Your Trend Report is complete. The download link will be available soon.
            </Alert>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default TrendReportDetailPage;